import React, { useState, useEffect, useCallback } from "react";
import MaterialTable from "material-table";
import { makeStyles } from "@mui/styles";
import {
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import RolesAPI from "../common/rolesApi";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "20px",
  },
  materialTable: {
    "& .MuiTableRow-root": {
      cursor: "pointer",
    },
    "& .MuiTableCell-root": {
      borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
  },
  roleChip: {
    fontWeight: "bold",
    color: "white",
  },
  adminChip: {
    backgroundColor: "#f44336",
  },
  fleetAdminChip: {
    backgroundColor: "#ff9800",
  },
  customerChip: {
    backgroundColor: "#4caf50",
  },
  driverChip: {
    backgroundColor: "#2196f3",
  },
  customRoleChip: {
    backgroundColor: colors.MAIN_COLOR,
  },
  activeChip: {
    backgroundColor: "#4CAF50",
    color: "white",
  },
  inactiveChip: {
    backgroundColor: "#F44336",
    color: "white",
  },
}));

export default function UsersTab() {
  const classes = useStyles();
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await RolesAPI.getUsersWithRoles();

      if (response.success) {
        setUsers(response.users || []);
        setRoles(response.roles || {});
      } else {
        showSnackbar(t("error_loading_users"), "error");
      }
    } catch (error) {
      console.error("Error loading users:", error);
      showSnackbar(t("error_loading_users"), "error");
    } finally {
      setLoading(false);
    }
  }, [t, showSnackbar]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAssignRole = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.customRole || "");
    setAssignDialogOpen(true);
  };

  const handleSaveRoleAssignment = async () => {
    try {
      const response = selectedRole
        ? await RolesAPI.assignUserRole(selectedUser.id, selectedRole)
        : await RolesAPI.removeUserRole(selectedUser.id);

      if (response.success) {
        showSnackbar(
          selectedRole
            ? t("role_assigned_successfully")
            : t("role_removed_successfully")
        );
        setAssignDialogOpen(false);
        setSelectedUser(null);
        setSelectedRole("");
        loadUsers();
      } else {
        showSnackbar(response.error || t("error_assigning_role"), "error");
      }
    } catch (error) {
      console.error("Error assigning role:", error);
      showSnackbar(t("error_assigning_role"), "error");
    }
  };

  const getUserTypeChipClass = (userType) => {
    switch (userType) {
      case "admin":
        return classes.adminChip;
      case "fleetadmin":
        return classes.fleetAdminChip;
      case "driver":
        return classes.driverChip;
      case "customer":
      default:
        return classes.customerChip;
    }
  };

  const renderUserTypeChip = (userType) => (
    <Chip
      label={t(userType)}
      className={`${classes.roleChip} ${getUserTypeChipClass(userType)}`}
      size="small"
    />
  );

  const renderCustomRoleChip = (customRole, customRoleName) => {
    if (!customRole || !customRoleName) return "-";

    return (
      <Chip
        label={customRoleName}
        className={`${classes.roleChip} ${classes.customRoleChip}`}
        size="small"
      />
    );
  };

  const renderStatusChip = (approved) => (
    <Chip
      label={approved ? t("active") : t("inactive")}
      className={approved ? classes.activeChip : classes.inactiveChip}
      size="small"
    />
  );

  const columns = [
    {
      title: t("first_name"),
      field: "firstName",
      cellStyle: { textAlign: "left" },
    },
    {
      title: t("last_name"),
      field: "lastName",
      cellStyle: { textAlign: "left" },
    },
    {
      title: t("email"),
      field: "email",
      cellStyle: { textAlign: "left" },
    },
    {
      title: t("mobile"),
      field: "mobile",
      cellStyle: { textAlign: "left" },
    },
    {
      title: t("usertype"),
      field: "usertype",
      render: (rowData) => renderUserTypeChip(rowData.usertype),
      cellStyle: { textAlign: "center" },
    },
    {
      title: t("custom_role"),
      field: "customRoleName",
      render: (rowData) =>
        renderCustomRoleChip(rowData.customRole, rowData.customRoleName),
      cellStyle: { textAlign: "center" },
    },
    {
      title: t("status"),
      field: "approved",
      render: (rowData) => renderStatusChip(rowData.approved),
      cellStyle: { textAlign: "center" },
    },
    {
      title: t("created_at"),
      field: "createdAt",
      type: "datetime",
      render: (rowData) => {
        if (!rowData.createdAt) return "-";
        return new Date(rowData.createdAt).toLocaleDateString();
      },
      cellStyle: { textAlign: "center" },
    },
  ];

  const tableActions = [
    {
      icon: "assignment",
      tooltip: t("assign_role"),
      onClick: (event, rowData) => handleAssignRole(rowData),
    },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.container}>
      <Box mb={3}>
        <Typography
          variant="h5"
          style={{ fontWeight: "bold", marginBottom: "8px" }}
        >
          {t("users_management")}
        </Typography>
        <Typography variant="body1" style={{ color: "#666" }}>
          {t("manage_user_roles_description")}
        </Typography>
      </Box>

      <MaterialTable
        title={`${t("all_users")} (${users.length})`}
        columns={columns}
        data={users}
        actions={tableActions}
        options={{
          exportButton: true,
          actionsColumnIndex: -1,
          pageSize: 10,
          pageSizeOptions: [10, 25, 50, 100],
          rowStyle: {
            fontSize: "0.875rem",
          },
          headerStyle: {
            backgroundColor: colors.MAIN_COLOR,
            color: "#fff",
            fontWeight: "bold",
          },
          actionsCellStyle: {
            paddingLeft: "20px",
            paddingRight: "20px",
          },
        }}
        className={classes.materialTable}
      />

      {/* Role Assignment Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("assign_role_to_user")}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box mb={2}>
              <Typography variant="body1">
                <strong>{t("user")}:</strong> {selectedUser.firstName}{" "}
                {selectedUser.lastName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedUser.email}
              </Typography>
            </Box>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>{t("select_role")}</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label={t("select_role")}
            >
              <MenuItem value="">
                <em>{t("no_custom_role")}</em>
              </MenuItem>
              {Object.entries(roles).map(([roleId, role]) => (
                <MenuItem key={roleId} value={roleId} disabled={!role.isActive}>
                  {role.name} {!role.isActive && "(Inactive)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSaveRoleAssignment}
            variant="contained"
            color="primary"
          >
            {t("assign")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
